import React, { useState, useContext, useEffect } from "react";
import styled from "styled-components";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  TextField,
  Button,
  IconButton,
  InputAdornment,
  CircularProgress,
  Typography,
} from "@material-ui/core";
import { Close, FileCopy, CheckCircle, Send } from "@material-ui/icons";
import Axios from "axios";
import { toast } from "react-toastify";
import { useHistory } from "react-router-dom";
import AppContext from "../../context/store";
import IyzicoLogoSvg from "../../assets/images/svg/iyizico-logo.svg";
import IyzicoSetupDialog from "./IyzicoSetupDialog";

const IyzicoLinkCreate = ({ open, onClose }) => {
  const context = useContext(AppContext);
  const history = useHistory();
  const [amount, setAmount] = useState("");
  const [paymentLink, setPaymentLink] = useState("");
  const [loading, setLoading] = useState(false);
  const [copied, setCopied] = useState(false);
  const [showSetupDialog, setShowSetupDialog] = useState(false);
  const [checkingSetup, setCheckingSetup] = useState(true);

  const handleCreateLink = async () => {
    if (!amount || amount <= 0) {
      toast.error(context.t(`iyzicoDialog.errorMessage`));
      return;
    }

    setLoading(true);
    try {
      const response = await Axios.post(
        `${context.api_endpoint}/company/balance/iyzicolink`,
        {
          amount: parseFloat(amount),
        }
      );

      if (response.data.status === 200) {
        setPaymentLink(response.data.data.payment_link);
        toast.success(context.t(`iyzicoDialog.successMessage`));
      }
    } catch (error) {
      console.error("Ödeme linki oluşturma hatası:", error);
      toast.error(
        error.response?.data?.message || context.t(`iyzicoDialog.errorMessage`)
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (open) {
      checkIyzicoSetup();
    }
  }, [open]);

  const checkIyzicoSetup = async () => {
    setCheckingSetup(true);
    try {
      const response = await Axios.get(
        `${context.api_endpoint}/company/itself`
      );

      if (response.data.status === 201) {
        const { iyzico_api_key, iyzico_secret_key } = response.data.data;

        // console.log("İyzico API Key:", iyzico_api_key);
        // console.log("İyzico Secret Key:", iyzico_secret_key);

        // API key veya secret key boşsa setup dialog'u göster
        if (!iyzico_api_key || !iyzico_secret_key ||
          iyzico_api_key.trim() === "" || iyzico_secret_key.trim() === "") {
          console.log("Setup dialog gösteriliyor...");
          setShowSetupDialog(true);
        } else {
          // console.log("API anahtarları mevcut, normal dialog gösteriliyor");
        }
      }
    } catch (error) {
      // console.error("İşletme bilgileri alınırken hata:", error);
      toast.error("İşletme bilgileri alınırken bir hata oluştu.");
    } finally {
      setCheckingSetup(false);
    }
  };

  const handleCopyLink = () => {
    navigator.clipboard.writeText(paymentLink);
    setCopied(true);
    toast.success(context.t(`iyzicoDialog.copySuccessMessage`));
    setTimeout(() => setCopied(false), 2000);
  };

  const handleSendSMS = () => {
    const messageWithLink = `${amount} tutarlı ödeme linkiniz başarıyla oluşturuldu.\n${paymentLink}`;
    history.push({
      pathname: "/sms",
      state: {
        paymentLink: paymentLink,
        amount: amount,
        fullMessage: messageWithLink
      }
    });
    handleClose();
  };

  const handleClose = () => {
    setAmount("");
    setPaymentLink("");
    setCopied(false);
    setShowSetupDialog(false);
    setCheckingSetup(true);
    onClose();
  };

  const handleSetupComplete = () => {
    setShowSetupDialog(false);
    // Kullanıcıyı ayarlar sayfasına yönlendir
    history.push("/settings");
    handleClose();
  };

  // Setup dialog açıksa onu göster
  if (showSetupDialog) {
    return (
      <IyzicoSetupDialog
        open={open}
        onClose={handleClose}
        onComplete={handleSetupComplete}
      />
    );
  }


  return (
    <StyledDialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <StyledDialogTitle disableTypography>
        <LeftSection>
          <IyzicoLogo>
            <img src={IyzicoLogoSvg} alt="iyzico" />
          </IyzicoLogo>
          <TitleText>{context.t(`iyzicoDialog.title`)}</TitleText>
        </LeftSection>
        <CloseButton onClick={handleClose} size="small">
          <Close />
        </CloseButton>
      </StyledDialogTitle>

      <StyledDialogContent>
        {checkingSetup ? (
          <LoadingContainer>
            <CircularProgress size={40} />
            <Typography variant="body2" style={{ marginTop: 16, color: "#666" }}>
              İyzico ayarları kontrol ediliyor...
            </Typography>
          </LoadingContainer>
        ) : (
          <>
            <InfoBox>
              <Typography variant="body2">
                {context.t(`iyzicoDialog.description`)}
              </Typography>
            </InfoBox>

            <InputContainer>
              <StyledTextField
                label={context.t(`iyzicoDialog.amountLabel`)}
                type="number"
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
                fullWidth
                variant="outlined"
                placeholder={context.t(`iyzicoDialog.amountPlaceholder`)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">₺</InputAdornment>
                  ),
                }}
                disabled={loading}
              />
            </InputContainer>

            {!paymentLink ? (
              <CreateButton
                variant="contained"
                onClick={handleCreateLink}
                disabled={loading}
                fullWidth
              >
                {loading ? (
                  <CircularProgress size={24} color="inherit" />
                ) : (
                  context.t(`iyzicoDialog.createLinkButton`)
                )}
              </CreateButton>
            ) : (
              <LinkContainer>
                <LinkLabel>{context.t(`iyzicoDialog.linkLabel`)}</LinkLabel>
                <LinkInputWrapper>
                  <StyledTextField
                    value={paymentLink}
                    fullWidth
                    variant="outlined"
                    InputProps={{
                      readOnly: true,
                    }}
                  />
                </LinkInputWrapper>
                <ButtonsRow>
                  <CopyButtonLarge
                    variant="contained"
                    onClick={handleCopyLink}
                    startIcon={copied ? <CheckCircle /> : <FileCopy />}
                  >
                    {copied ? context.t(`iyzicoDialog.copyButtonCopied`) : context.t(`iyzicoDialog.copyButton`)}
                  </CopyButtonLarge>
                  <SendSMSButton
                    variant="contained"
                    onClick={handleSendSMS}
                    startIcon={<Send />}
                  >
                    {context.t(`iyzicoDialog.sendSMSButton`)}
                  </SendSMSButton>
                </ButtonsRow>
                <CreateAnotherButtonRow>
                  <CreateAnotherButton
                    variant="outlined"
                    onClick={() => {
                      setPaymentLink("");
                      setAmount("");
                      setCopied(false);
                    }}
                  >
                    {context.t(`iyzicoDialog.createAnotherButton`)}
                  </CreateAnotherButton>
                </CreateAnotherButtonRow>
              </LinkContainer>
            )}
          </>
        )}
      </StyledDialogContent>
    </StyledDialog>
  );
};

export default IyzicoLinkCreate;

const StyledDialog = styled(Dialog)`
  .MuiDialog-paper {
    border-radius: 8px;
    box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);
  }
`;

const StyledDialogTitle = styled(DialogTitle)`
  display: flex !important;
  flex-direction: row !important;
  justify-content: space-between !important;
  align-items: center !important;
  padding: 16px 20px !important;
  border-bottom: 1px solid #e0e0e0 !important;
  background: white !important;
  color: #333 !important;
`;

const LeftSection = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  flex: 1;
`;

const TitleText = styled.span`
  font-size: 16px;
  font-weight: 600;
  color: #333;
`;

const IyzicoLogo = styled.div`
  display: flex;
  align-items: center;
  
  img {
    height: 28px;
    width: auto;
  }
`;

const CloseButton = styled(IconButton)`
  color: #666 !important;
  padding: 6px !important;
  margin: 0 !important;
  
  &:hover {
    background: rgba(0, 0, 0, 0.05) !important;
  }
  
  svg {
    font-size: 20px;
  }
`;

const StyledDialogContent = styled(DialogContent)`
  padding: 20px !important;
  background: white;
`;

const InfoBox = styled.div`
  background: #f5f5f5;
  padding: 12px;
  border-radius: 6px;
  margin-bottom: 20px;
  border-left: 3px solid #667eea;

  .MuiTypography-root {
    color: #666;
    font-size: 14px;
  }
`;

const InputContainer = styled.div`
  margin-bottom: 20px;
`;

const StyledTextField = styled(TextField)`
  .MuiOutlinedInput-root {
    background: white;
    border-radius: 6px;

    &:hover fieldset {
      border-color: #667eea;
    }

    &.Mui-focused fieldset {
      border-color: #667eea;
    }
  }

  input[type="number"]::-webkit-inner-spin-button,
  input[type="number"]::-webkit-outer-spin-button {
    -webkit-appearance: none;
    margin: 0;
  }
`;

const CreateButton = styled(Button)`
  background: #667eea !important;
  color: white !important;
  padding: 10px !important;
  border-radius: 6px !important;
  font-weight: 500 !important;
  font-size: 14px !important;
  text-transform: none !important;
  box-shadow: none !important;
  transition: all 0.2s ease !important;

  &:hover {
    background: #5568d3 !important;
  }

  &:disabled {
    background: #e0e0e0 !important;
    color: #9e9e9e !important;
  }
`;

const LinkContainer = styled.div`
  margin-top: 24px;
`;

const LinkLabel = styled.div`
  font-size: 14px;
  font-weight: 600;
  color: #333;
  margin-bottom: 8px;
`;

const LinkInputWrapper = styled.div`
  margin-bottom: 12px;
`;

const ButtonsRow = styled.div`
  display: flex;
  gap: 12px;
  margin-top: 12px;
`;

const CopyButtonLarge = styled(Button)`
  flex: 1;
  background: #667eea !important;
  color: white !important;
  padding: 12px 20px !important;
  border-radius: 6px !important;
  font-weight: 600 !important;
  font-size: 15px !important;
  text-transform: none !important;
  box-shadow: none !important;
  transition: all 0.2s ease !important;

  &:hover {
    background: #5568d3 !important;
    transform: translateY(-1px);
    box-shadow: 0 2px 8px rgba(102, 126, 234, 0.3) !important;
  }

  &:active {
    transform: translateY(0);
  }

  .MuiButton-startIcon {
    margin-right: 8px;
  }
`;

const SendSMSButton = styled(Button)`
  flex: 1;
  background: #10b981 !important;
  color: white !important;
  padding: 12px 20px !important;
  border-radius: 6px !important;
  font-weight: 600 !important;
  font-size: 15px !important;
  text-transform: none !important;
  box-shadow: none !important;
  transition: all 0.2s ease !important;

  &:hover {
    background: #059669 !important;
    transform: translateY(-1px);
    box-shadow: 0 2px 8px rgba(16, 185, 129, 0.3) !important;
  }

  &:active {
    transform: translateY(0);
  }

  .MuiButton-startIcon {
    margin-right: 8px;
  }
`;

const CreateAnotherButtonRow = styled.div`
  margin-top: 12px;
`;

const CreateAnotherButton = styled(Button)`
  width: 100%;
  color: #667eea !important;
  border-color: #667eea !important;
  padding: 12px 20px !important;
  border-radius: 6px !important;
  font-weight: 600 !important;
  font-size: 15px !important;
  text-transform: none !important;
  transition: all 0.2s ease !important;
  border-width: 2px !important;

  &:hover {
    background: rgba(102, 126, 234, 0.08) !important;
    border-width: 2px !important;
    transform: translateY(-1px);
  }

  &:active {
    transform: translateY(0);
  }
`;

const LoadingContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 40px 20px;
  min-height: 200px;
`;
